// Typed helpers for the sender screener (/api/senders). URL builders are pure so they can be
// unit-tested; addresses travel as one encoded path segment.
import type { SenderView } from "@shared/api";
import type { Destination } from "@shared/routing";
import { api } from "./api";

const enc = encodeURIComponent;

export type SenderScreen = SenderView["screen"];

export interface SenderListParams {
  accountId?: string | undefined;
  screen?: SenderScreen | undefined;
  /** Only first-time senders waiting for a decision. */
  awaiting?: boolean | undefined;
  limit?: number | undefined;
}

export const senderUrls = {
  list: (params: SenderListParams) => {
    const search = new URLSearchParams();
    if (params.accountId) search.set("accountId", params.accountId);
    if (params.screen) search.set("screen", params.screen);
    if (params.awaiting) search.set("awaiting", "1");
    if (params.limit) search.set("limit", String(params.limit));
    const text = search.toString();
    return `/api/senders${text ? `?${text}` : ""}`;
  },
  sender: (accountId: string, address: string) =>
    `/api/senders/${enc(accountId)}/${enc(address.toLowerCase())}`,
  screen: (accountId: string, address: string) =>
    `/api/senders/${enc(accountId)}/${enc(address.toLowerCase())}/screen`,
  destination: (accountId: string, address: string) =>
    `/api/senders/${enc(accountId)}/${enc(address.toLowerCase())}/destination`,
};

export const sendersApi = {
  list: (params: SenderListParams, signal?: AbortSignal) =>
    api<SenderView[]>(senderUrls.list(params), { signal }),
  get: (accountId: string, address: string, signal?: AbortSignal) =>
    api<SenderView>(senderUrls.sender(accountId, address), { signal }),
  screen: (accountId: string, address: string, screen: SenderScreen) =>
    api<SenderView>(senderUrls.screen(accountId, address), { method: "POST", body: { screen } }),
  /** U8: Cards, Brief only (with an optional delivery), Receipts or Screen out. */
  destination: (
    accountId: string,
    address: string,
    destination: Destination | null,
    delivery?: string | null,
  ) =>
    api<SenderView>(senderUrls.destination(accountId, address), {
      method: "POST",
      body: { destination, ...(delivery !== undefined ? { delivery } : {}) },
    }),
};
